import {
  type BillingCurrency,
  type BillingRail,
  displayUsdCentsFromTtd,
  usdCentsToTtdCents,
} from "../../../convex/lib/billingRail";

export type DisplayCurrencyState = {
  currency: BillingCurrency;
  rail: BillingRail;
  fxUsdToTtd: number | null;
  country?: string;
};

const DEFAULT_STATE: DisplayCurrencyState = {
  currency: "TTD",
  rail: "wam",
  fxUsdToTtd: null,
};

let state: DisplayCurrencyState = DEFAULT_STATE;

export function getDisplayCurrency(): DisplayCurrencyState {
  return state;
}

export function setDisplayCurrency(next: DisplayCurrencyState): void {
  state = next;
}

function formatFace(amountCents: number, currency: BillingCurrency, short: boolean): string {
  const amount = amountCents / 100;
  const face = `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
  if (short && currency === "TTD") return face;
  return `${face} ${currency}`;
}

/** Ledger is always TTD cents. Display follows the wallet country. */
export function formatLedgerCents(
  ttdCents: number | null | undefined,
  opts?: { short?: boolean; state?: DisplayCurrencyState },
): string {
  if (ttdCents == null || Number.isNaN(Number(ttdCents))) return "—";
  const snap = opts?.state ?? state;
  const short = opts?.short === true;
  if (snap.currency === "USD") {
    if (!snap.fxUsdToTtd) return short ? "USD…" : "USD pending";
    return formatFace(displayUsdCentsFromTtd(Number(ttdCents), snap.fxUsdToTtd), "USD", short);
  }
  return formatFace(Number(ttdCents), "TTD", short);
}

export function displayCentsFromLedger(
  ttdCents: number,
  snap: DisplayCurrencyState = state,
): number {
  if (snap.currency === "USD") {
    if (!snap.fxUsdToTtd) return 0;
    return displayUsdCentsFromTtd(ttdCents, snap.fxUsdToTtd);
  }
  return ttdCents;
}

export function ledgerCentsFromDisplayInput(
  displayDollars: number,
  snap: DisplayCurrencyState = state,
): number {
  const displayCents = Math.round(Number(displayDollars || 0) * 100);
  if (!Number.isFinite(displayCents) || displayCents <= 0) return 0;
  if (snap.currency === "USD") {
    if (!snap.fxUsdToTtd) return 0;
    return usdCentsToTtdCents(displayCents, snap.fxUsdToTtd);
  }
  return displayCents;
}

export function displayInputFromLedgerCents(
  ttdCents: number,
  snap: DisplayCurrencyState = state,
): string {
  const cents = displayCentsFromLedger(ttdCents, snap);
  const dollars = cents / 100;
  if (!Number.isFinite(dollars) || dollars <= 0) return "";
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
}

export function displayCurrencyCode(snap: DisplayCurrencyState = state): BillingCurrency {
  return snap.currency;
}
